import React, { useContext } from "react";
import PropTypes from "prop-types";
import AuthContext from "connections/auth/AuthProvider.js";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  FormControl,
  MenuItem,
  Select,
  makeStyles,
} from "@material-ui/core";
import {
  grayColor,
  primaryColor,
} from "assets/jss/material-dashboard-pro-react.js";
import customSelectStyle from "assets/jss/material-dashboard-pro-react/customSelectStyle.js";
import customInputStyle from "assets/jss/material-dashboard-pro-react/components/customInputStyle.js";
import Card from "components/Card/Card.js";
import CardBody from "components/Card/CardBody.js";
import CardHeader from "components/Card/CardHeader.js";

const sectionHeadersDefinitionStyle = (theme, clientColor) => ({
  ...customSelectStyle(theme, clientColor),
  ...customInputStyle(clientColor),

  mainContainer: {
    minHeight: 300,
    position: "relative", // Establish a stacking context for the section
    zIndex: 0, // Lower z-index than other sections
  },
  mainSectionTitle: {
    fontWeight: 600,
    fontSize: "1.125rem",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    color: grayColor[2],
  },
  row: {
    display: "flex",
    alignItems: "center",
    marginBottom: theme.spacing(2),
    marginLeft: theme.spacing(1),
  },
  column: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  smallText: {
    fontSize: "0.875rem",
    color: grayColor[1],
  },
  inputLabel: {
    width: 160,
    fontWeight: 400,
    fontSize: "0.875rem",
    color: grayColor[2],
    textTransform: "capitalize",
  },
  select: {
    height: 40,
    display: "flex",
    alignItems: "center",
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
  inputLabelRoot: {
    color: grayColor[1],
    "&.Mui-focused": {
      color: primaryColor[0],
    },
  },
  columnGroupCard: {
    border: `1px solid ${grayColor[5]}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: 200,
    display: "flex",
    flexDirection: "column",
    position: "static", // Override the default relative positioning from cardStyle
    zIndex: 1, // Ensure cards don't float above other sections
    flex: "0 0 calc(33.33% - 21.33px)", // Allow cards to grow/shrink with min-width of 300px
    marginBottom: 0,
    marginTop: 0,
    "& .MuiCardHeader-root": {
      padding: theme.spacing(2),
      paddingBottom: 0,
    },
    "& .MuiCardContent-root": {
      padding: theme.spacing(2),
      paddingTop: 0,
      flexGrow: 1, // Allow CardBody to grow and fill space
      "&:last-child": {
        paddingBottom: theme.spacing(2),
      },
    },
  },
  columnGroupTitle: {
    fontWeight: 400,
    fontSize: "1rem",
    margin: 0,
    color: grayColor[2],
    textTransform: "capitalize",
  },
  cardsContainer: {
    display: "flex",
    flexWrap: "wrap",
    columnGap: theme.spacing(2),
    marginBottom: theme.spacing(4),
    minHeight: "auto",
    alignItems: "stretch",
    gap: theme.spacing(2),
  },
});

export default function SectionHeadersDefinition({
  readonly,
  currentTab,
  currentMappings,
  activeColumns,
  fileTabs,
  newFileSheet,
  newFileHeaders,
  localMappings,
  onChangeTab,
  onMappingChange,
}) {
  // Guard clause: If activeColumns is not defined, return null to prevent errors
  if (!activeColumns) {
    return null;
  }

  const { userDetails } = useContext(AuthContext);
  const client_styles = (theme) => ({
    ...sectionHeadersDefinitionStyle(theme, userDetails.implementation_color),
  });
  const useStyles = makeStyles(client_styles);
  const classes = useStyles();

  const T_PREFIX = "rgm.inputs.headers_definition";
  const { t } = useTranslation("translation", { keyPrefix: T_PREFIX });

  // Group columns by column_group
  const groupedColumns = Object.entries(activeColumns)
    .filter(([fieldKey, colDef]) => {
      // Filter: Only show columns that come from file inputs
      // Exclude: variant_id, and columns without column_source='file_inputs'
      return (
        colDef &&
        fieldKey !== "variant_id" &&
        colDef.column_source === "file_inputs"
      );
    })
    .reduce((acc, [fieldKey, colDef]) => {
      const group = colDef.column_group;
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push({ fieldKey, colDef });
      return acc;
    }, {});

  return (
    <Box
      display="flex"
      flexDirection="column"
      gap={2}
      className={classes.mainContainer}
    >
      {readonly ? (
        <>
          <Box className={classes.row}>
            <Typography className={classes.inputLabel}>
              {t("sheet_tab")}
            </Typography>
            <Typography variant="body2" className={classes.smallText}>
              <i>{currentTab || " - "}</i>
            </Typography>
          </Box>
          <Box className={classes.cardsContainer}>
            {Object.entries(groupedColumns).map(([groupName, columns]) => (
              <Card className={classes.columnGroupCard} key={groupName}>
                <CardHeader>
                  <Typography variant="h6" className={classes.columnGroupTitle}>
                    {groupName}
                  </Typography>
                </CardHeader>
                <CardBody>
                  {columns.map(({ fieldKey, colDef }) => (
                    <Box
                      key={fieldKey}
                      display="flex"
                      alignItems="center"
                      gap={2}
                      mb={1}
                    >
                      <Typography className={classes.inputLabel}>
                        {colDef.column_name || fieldKey.replace(/_/g, " ")}
                      </Typography>
                      <Typography className={classes.smallText}>
                        {currentMappings[fieldKey] || "–"}
                      </Typography>
                    </Box>
                  ))}
                </CardBody>
              </Card>
            ))}
          </Box>
        </>
      ) : (
        <>
          {/* Sheet Selector */}
          <Box className={classes.row}>
            <Typography className={classes.inputLabel}>
              {t("sheet_tab")}
            </Typography>
            <FormControl
              fullWidth
              className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
              style={{ width: "30%" }}
            >
              <Select
                value={newFileSheet}
                onChange={(e) => onChangeTab(e.target.value)}
                MenuProps={{
                  className: classes.selectMenu,
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                classes={{
                  select: `${classes.select} ${classes.selectFormControlOverride}`,
                }}
              >
                <MenuItem value="">
                  <em>-</em>
                </MenuItem>
                {fileTabs.map((tab) => (
                  <MenuItem
                    key={tab}
                    value={tab}
                    classes={{
                      root: classes.selectMenuItem,
                      selected: classes.selectMenuItemSelected,
                    }}
                  >
                    {tab}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Column mappings */}
          <Box className={classes.cardsContainer}>
            {/* Groups horizontally */}
            {Object.entries(groupedColumns).map(([groupName, columns]) => (
              <Card className={classes.columnGroupCard} key={groupName}>
                <CardHeader>
                  <Typography variant="h6" className={classes.columnGroupTitle}>
                    {groupName}
                  </Typography>
                </CardHeader>
                <CardBody>
                  {columns.map(({ fieldKey, colDef }) => (
                    <Box
                      key={fieldKey}
                      display="flex"
                      alignItems="center"
                      gap={2}
                    >
                      {" "}
                      {/* Changed to flex row */}
                      <Typography className={classes.inputLabel}>
                        {colDef.column_name || fieldKey.replace(/_/g, " ")}
                      </Typography>
                      <FormControl
                        fullWidth
                        className={`${classes.selectFormControl} ${classes.selectFormControlOverride}`}
                      >
                        <Select
                          value={localMappings[fieldKey] || ""}
                          onChange={(e) =>
                            onMappingChange(fieldKey, e.target.value)
                          }
                          MenuProps={{
                            className: classes.selectMenu,
                            anchorOrigin: {
                              vertical: "bottom",
                              horizontal: "left",
                            },
                            getContentAnchorEl: null,
                          }}
                          classes={{
                            select: `${classes.select} ${classes.selectFormControlOverride}`,
                          }}
                        >
                          <MenuItem value="">
                            <em>-</em>
                          </MenuItem>
                          {newFileHeaders.map((col) => (
                            <MenuItem
                              key={col}
                              value={col}
                              classes={{
                                root: classes.selectMenuItem,
                                selected: classes.selectMenuItemSelected,
                              }}
                            >
                              {col}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Box>
                  ))}
                </CardBody>
              </Card>
            ))}
          </Box>
        </>
      )}
    </Box>
  );
}

SectionHeadersDefinition.propTypes = {
  readonly: PropTypes.bool.isRequired,
  currentTab: PropTypes.string,
  currentMappings: PropTypes.object.isRequired,
  activeColumns: PropTypes.object.isRequired,
  fileTabs: PropTypes.array.isRequired,
  newFileSheet: PropTypes.string.isRequired,
  newFileHeaders: PropTypes.array.isRequired,
  localMappings: PropTypes.object.isRequired,
  onChangeTab: PropTypes.func.isRequired,
  onMappingChange: PropTypes.func.isRequired,
};
